import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, X } from "lucide-react";

// Confirmación de borrado en formato drawer.
// Desktop: panel desde la derecha (estrecho).
// Mobile: bottom sheet compacto.

export default function ConfirmDeleteDrawer({
  title = "Eliminar elemento",
  itemName,
  message = "¿Estás seguro de que quieres eliminar este elemento?",
  warningText = "Esta acción no se puede deshacer.",
  confirmLabel = "Eliminar",
  cancelLabel = "Cancelar",
  onConfirm,
  onCancel,
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => setVisible(true));
    return () => { document.body.style.overflow = ""; };
  }, []);

  const close = (cb) => {
    setVisible(false);
    setTimeout(cb, 200);
  };

  const handleCancel = () => close(onCancel);
  const handleConfirm = () => close(onConfirm);

  const content = (
    <div className="flex flex-col h-full md:h-auto">
      <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
            <AlertTriangle size={20} className="text-red-600" />
          </div>
          <h3 className="font-semibold text-gray-900">{title}</h3>
        </div>
        <button
          onClick={handleCancel}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 px-6 py-5 overflow-y-auto">
        <p className="text-sm text-gray-600 mb-3">{message}</p>

        {itemName && (
          <p className="text-sm font-medium text-gray-900 bg-gray-50 px-3 py-2 rounded-lg mb-3">
            {itemName}
          </p>
        )}

        {warningText && (
          <p className="text-xs text-gray-500">{warningText}</p>
        )}
      </div>

      <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
        <button
          onClick={handleCancel}
          className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
        >
          {cancelLabel}
        </button>
        <button
          onClick={handleConfirm}
          className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors"
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  );

  const panel = (
    <div className="fixed inset-0 z-[99999]">
      <div
        className={`fixed inset-0 bg-black/40 transition-opacity duration-200 ${visible ? "opacity-100" : "opacity-0"}`}
        onClick={handleCancel}
      />

      {/* Desktop: drawer desde la derecha */}
      <div
        className={`
          hidden md:flex fixed top-0 right-0 h-full w-full max-w-md
          flex-col bg-white shadow-2xl
          transition-transform duration-200 ease-out
          ${visible ? "translate-x-0" : "translate-x-full"}
        `}
      >
        {content}
      </div>

      {/* Mobile: bottom sheet compacto */}
      <div
        className={`
          md:hidden fixed inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-2xl
          transition-transform duration-200 ease-out
          ${visible ? "translate-y-0" : "translate-y-full"}
        `}
      >
        {content}
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}
